import { Spinner } from '@/components/Spinner';
import { Can } from '@/components/can/Can';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation } from '@/hoslog-intl';
import { EditIcon } from '@/icons/edit';
import { PlusIcon } from '@/icons/plus-icon';
import { Link } from '@tanstack/react-router';
import { useBankInformation } from '../hooks/useBankInformation';
import { getBankInformation } from './admin.utils';

export const AdminLogs = () => {
  const { bankInformation, isPending } = useBankInformation();
  const bankFields = getBankInformation(bankInformation);
  const hasBankInfo = Boolean(
    bankInformation?.bankName ||
      bankInformation?.accountNumber ||
      bankInformation?.branchName ||
      bankInformation?.accountName,
  );

  if (isPending) return <Spinner />;

  return (
    <section className="rounded-lg bg-card p-6 shadow-sm md:p-8">
      <div className="pb-3 flex items-center justify-between border-b border-border-divider mb-6">
        <h3 className="text-base font-bold text-foreground-heading">
          <Translation id="paymentManagement.adminPaymentSetup.adminBankInfo.title" />
        </h3>
        <Can rule={{ allOf: [PERMS.paymentManagement.adminPaymentSetup.manage] }}>
          <Link to="/payment-management/admin-payment-setup/add-bank-information">
            {hasBankInfo ? (
              <Button size="sm">
                <EditIcon className="h-4 w-4" />
                <Translation id="common.page.button.update" />
              </Button>
            ) : (
              <Button size="sm">
                <PlusIcon className="h-4 w-4" />
                <Translation id="common.page.button.addNew" />
              </Button>
            )}
          </Link>
        </Can>
      </div>

      <div className="flex flex-col items-stretch gap-8 lg:flex-row lg:items-end lg:gap-12 xl:gap-20">
        <div className="w-full flex-1 rounded border border-border-divider bg-surface-subtle p-5 md:p-6">
          <dl className="space-y-6">
            {bankFields.map(({ id, label, value }) => (
              <div key={id} className="border-b border-border-divider pb-3 last:border-b-0 last:pb-0">
                <div className="flex items-center gap-4 sm:gap-16">
                  <dt className="min-w-28 shrink-0 text-sm font-medium text-foreground-label sm:min-w-40">
                    <Translation id={label} />
                  </dt>
                  <dd className="text-sm font-bold text-foreground-heading">{value || '-'}</dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
        <div className="w-full rounded bg-destructive/20 p-5 md:p-6 lg:max-w-xs lg:shrink-0">
          <h3 className="mb-4 text-sm font-bold text-foreground-heading">
            <Translation id="common.form.label.note" defaultMessage="Note" />:
          </h3>
          <p className="text-xs leading-[17.4px] text-overlay">
            <Translation
              id="paymentManagement.adminPaymentSetup.adminBankInfo.noteDescription"
              defaultMessage="The updated Bank information can be displayed only in store usage fee payments."
            />
          </p>
        </div>
      </div>
    </section>
  );
};
